/**
 * Share to WhatsApp (Oct 2026) — the links and the ready-to-send messages.
 *
 * Strategy: WhatsApp is where clubs already talk, so instead of fighting it
 * every share drops a Marakin link INTO the group. The message carries the
 * essentials (so it's useful even unopened) and ends with a tap-to-reply link
 * back into the app — that's the traffic loop.
 *
 * Links are short, share-only paths that the backend recognises for link
 * previews (lib/shareMeta.js) and the app routes into the hub
 * (pages/SharedLinkRedirect.jsx):
 *   /:club/e/:eventId   a fixture / training session / event
 *   /:club/n/:annId     a news post
 * Invites keep their own /:club/invite/:token links.
 *
 * Always built on PUBLIC_WEB_URL — in the native app window.location.origin is
 * localhost and the link would be dead everywhere else (F-010).
 *
 * Texts use WhatsApp's *bold*; elsewhere the asterisks read fine as emphasis.
 */
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { PUBLIC_WEB_URL } from '../config/api';

const TYPE_ICON = { match: '🏆', training: '🏃', social: '🎉', general: '📅' };

export const eventShareUrl = (clubSlug, eventId) => `${PUBLIC_WEB_URL}/${clubSlug}/e/${eventId}`;
export const newsShareUrl = (clubSlug, annId) => `${PUBLIC_WEB_URL}/${clubSlug}/n/${annId}`;

export function eventShareText(ev, url) {
  const starts = parseISO(ev.starts_at);
  const isMatch = ev.event_type === 'match';
  const when = ev.meet_at
    ? `${format(starts, 'EEE d MMM')} · Meet ${format(new Date(ev.meet_at), 'HH:mm')} · ${isMatch ? 'KO' : 'Starts'} ${format(starts, 'HH:mm')}`
    : `${format(starts, 'EEE d MMM')} · ${isMatch ? 'KO ' : ''}${format(starts, 'HH:mm')}`;
  const team = ev.resolved_team_name || ev.team_name;
  // Fixture titles already carry the team ("U11s Clevedon Away"); training
  // and socials don't, so name the team on its own line.
  const teamLine = !isMatch && team && !String(ev.title).includes(team) ? `👥 ${team}` : null;
  const upcoming = starts > new Date();
  const rsvpOpen = upcoming && ev.rsvp_by && new Date(ev.rsvp_by) > new Date();
  return [
    `${TYPE_ICON[ev.event_type] || '📅'} *${ev.title}*`,
    teamLine,
    `🗓 ${when}`,
    ev.location ? `📍 ${ev.location}` : null,
    rsvpOpen ? `📣 Please reply by ${format(new Date(ev.rsvp_by), 'EEE d MMM')}` : null,
    '',
    upcoming ? 'Going or not? Tap to reply on Marakin 👇' : 'Details on Marakin 👇',
    url,
  ].filter(l => l !== null).join('\n');
}

// News text lives in a pure module so it can be unit-tested (B-52.2: a
// members-only post's message carries no title or body).
export { newsShareText } from './newsShareText';

export function inviteShareText(clubName, url, firstName) {
  return [
    `${firstName ? `Hi ${firstName}! ` : ''}You're invited to join *${clubName || 'the club'}* on Marakin — fixtures, availability and club news in one place.`,
    '',
    'Tap to join 👇',
    url,
  ].join('\n');
}

/** wa.me opens WhatsApp (app or web) with the message typed in, no recipient chosen. */
export const whatsappUrl = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;

/**
 * Fire-and-forget usage signal (content_shared). Only for signed-in people —
 * the route needs a user — and never blocks or surfaces an error.
 */
export function trackShare(clubSlug, { kind, targetId = null, channel }) {
  if (!clubSlug) return;
  axios.post(`/clubs/${clubSlug}/shares`, { kind, targetId, channel }).catch(() => {});
}

export function trackSharedLinkOpened(clubSlug, { kind, targetId }) {
  axios.post(`/clubs/${clubSlug}/shares/opened`, { kind, targetId }).catch(() => {});
}
